// /frontend/src/pages/profile/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import { updateProfile } from '../../store/slices/authSlice';

const ProfilePage = () => {
  const { user, isAuthenticated, loading } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Address state
  const [addressData, setAddressData] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });

  // UI states
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState('');

  // Populate form with user data when available
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Set address data if available
      if (user.address) {
        setAddressData({
          street: user.address.street || '',
          city: user.address.city || '',
          state: user.address.state || '',
          zipCode: user.address.zipCode || '',
          country: user.address.country || ''
        });
      }
    }
  }, [user]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      navigate('/login?redirect=/profile');
    }
  }, [isAuthenticated, loading, navigate]);

  // Handle input changes for profile form
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear messages when user starts typing
    setPasswordMessage('');
    setPasswordError('');
    setUpdateSuccess(false);
    setUpdateError('');
  };

  // Handle input changes for address form
  const handleAddressChange = (e) => {
    setAddressData({
      ...addressData,
      [e.target.name]: e.target.value
    });
    
    setUpdateSuccess(false);
    setUpdateError('');
  };

  // Handle profile update
  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    
    // Password validation
    if (formData.newPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        setPasswordError('New passwords do not match');
        return;
      }
      
      if (formData.newPassword.length < 8) {
        setPasswordError('New password must be at least 8 characters');
        return;
      }
      
      if (!formData.currentPassword) {
        setPasswordError('Current password is required to set a new password');
        return;
      }
    }
    
    try {
      // Create update data object
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      };
      
      // Add password info if changing password
      if (formData.newPassword && formData.currentPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }
      
      // Dispatch update action
      const resultAction = await dispatch(updateProfile(updateData));
      
      if (updateProfile.fulfilled.match(resultAction)) {
        setUpdateSuccess(true);
        setFormData({
          ...formData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setIsEditing(false);
        
        // Show success message
        setPasswordMessage('Profile updated successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setPasswordMessage('');
          setUpdateSuccess(false);
        }, 3000);
      } else {
        setUpdateError(resultAction.error.message || 'Failed to update profile');
      }
    } catch (error) {
      setUpdateError(error.message || 'An error occurred while updating profile');
    }
  };

  // Handle address update
  const handleSubmitAddress = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        address: { ...addressData }
      };
      
      // Dispatch update action
      const resultAction = await dispatch(updateProfile(updateData));
      
      if (updateProfile.fulfilled.match(resultAction)) {
        setUpdateSuccess(true);
        setIsEditingAddress(false);
        
        // Show success message
        setPasswordMessage('Address updated successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setPasswordMessage('');
          setUpdateSuccess(false);
        }, 3000);
      } else {
        setUpdateError(resultAction.error.message || 'Failed to update address');
      }
    } catch (error) {
      setUpdateError(error.message || 'An error occurred while updating address');
    }
  };

  // Cancel editing
  const handleCancel = () => {
    // Reset form data to current user data
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Reset address data
      if (user.address) {
        setAddressData({
          street: user.address.street || '',
          city: user.address.city || '',
          state: user.address.state || '',
          zipCode: user.address.zipCode || '',
          country: user.address.country || ''
        });
      }
    }
    
    // Reset UI states
    setIsEditing(false);
    setIsEditingAddress(false);
    setPasswordMessage('');
    setPasswordError('');
    setUpdateSuccess(false);
    setUpdateError('');
  };

  if (loading || !user) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-48 bg-gray-200 rounded mb-6"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Main Content */}
        <div className="w-full">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">My Profile</h2>
            </div>
            
            {/* Notification messages */}
            {passwordMessage && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 mx-6 mt-4 rounded flex items-center">
                <FaCheck className="mr-2" />
                <span>{passwordMessage}</span>
              </div>
            )}
            
            {updateError && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 mx-6 mt-4 rounded flex items-center">
                <FaTimes className="mr-2" />
                <span>{updateError}</span>
              </div>
            )}
            
            {/* Profile Information */}
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
                
                {/* Edit Button */}
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center text-teal-800 hover:text-teal-900"
                  >
                    <FaEdit className="mr-1" /> Edit
                  </button>
                )}
              </div>
              
              {/* Profile Form */}
              <form onSubmit={handleSubmitProfile}>
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  {/* First Name */}
                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <div className="mt-1">
                      {isEditing ? (
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      ) : (
                        <div className="py-2">{user.firstName || '-'}</div>
                      )}
                    </div>
                  </div>
                  
                  {/* Last Name */}
                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <div className="mt-1">
                      {isEditing ? (
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      ) : (
                        <div className="py-2">{user.lastName || '-'}</div>
                      )}
                    </div>
                  </div>
                  
                  {/* Email */}
                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <div className="mt-1">
                      <div className="py-2 text-gray-600">{user.email || '-'}</div>
                      {isEditing && (
                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed. Please contact support.</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Phone */}
                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Phone Number
                    </label>
                    <div className="mt-1">
                      {isEditing ? (
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      ) : (
                        <div className="py-2">{user.phone || '-'}</div>
                      )}
                    </div>
                  </div>
                  
                  {/* Password fields - only shown when editing */}
                  {isEditing && (
                    <>
                      <div className="col-span-6">
                        <h4 className="font-medium text-gray-900 mb-2">Change Password</h4>
                        <p className="text-sm text-gray-600 mb-4">Leave blank if you don't want to change your password</p>
                      </div>
                      
                      {/* Current Password */}
                      <div className="sm:col-span-6">
                        <label className="block text-sm font-medium text-gray-700">
                          Current Password
                        </label>
                        <div className="mt-1">
                          <input
                            type="password"
                            name="currentPassword"
                            value={formData.currentPassword}
                            onChange={handleChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                      </div>
                      
                      {/* New Password */}
                      <div className="sm:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">
                          New Password
                        </label>
                        <div className="mt-1">
                          <input
                            type="password"
                            name="newPassword"
                            value={formData.newPassword}
                            onChange={handleChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                      </div>
                      
                      {/* Confirm New Password */}
                      <div className="sm:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Confirm New Password
                        </label>
                        <div className="mt-1">
                          <input
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                      </div>
                      
                      {/* Password validation error */}
                      {passwordError && (
                        <div className="sm:col-span-6">
                          <p className="text-red-600 text-sm">{passwordError}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {/* Form Actions */}
                {isEditing && (
                  <div className="flex justify-end mt-6 space-x-3">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-800 hover:bg-teal-900"
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </form>
              
              {/* Address Information */}
              <div className="mt-12">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Address Information</h3>
                  
                  {/* Edit Button */}
                  {!isEditingAddress && (
                    <button
                      onClick={() => setIsEditingAddress(true)}
                      className="flex items-center text-teal-800 hover:text-teal-900"
                    >
                      <FaEdit className="mr-1" /> Edit
                    </button>
                  )}
                </div>
                
                {/* Address Form */}
                <form onSubmit={handleSubmitAddress}>
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    {/* Street */}
                    <div className="sm:col-span-6">
                      <label className="block text-sm font-medium text-gray-700">
                        Street Address
                      </label>
                      <div className="mt-1">
                        {isEditingAddress ? (
                          <input
                            type="text"
                            name="street"
                            value={addressData.street}
                            onChange={handleAddressChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        ) : (
                          <div className="py-2">{user.address?.street || '-'}</div>
                        )}
                      </div>
                    </div>
                    
                    {/* City */}
                    <div className="sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700">
                        City
                      </label>
                      <div className="mt-1">
                        {isEditingAddress ? (
                          <input
                            type="text"
                            name="city"
                            value={addressData.city}
                            onChange={handleAddressChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        ) : (
                          <div className="py-2">{user.address?.city || '-'}</div>
                        )}
                      </div>
                    </div>
                    
                    {/* State/Province */}
                    <div className="sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700">
                        State / Province
                      </label>
                      <div className="mt-1">
                        {isEditingAddress ? (
                          <input
                            type="text"
                            name="state"
                            value={addressData.state}
                            onChange={handleAddressChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        ) : (
                          <div className="py-2">{user.address?.state || '-'}</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Zip/Postal Code */}
                    <div className="sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700">
                        ZIP / Postal Code
                      </label>
                      <div className="mt-1">
                        {isEditingAddress ? (
                          <input
                            type="text"
                            name="zipCode"
                            value={addressData.zipCode}
                            onChange={handleAddressChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        ) : (
                          <div className="py-2">{user.address?.zipCode || '-'}</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Country */}
                    <div className="sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Country
                      </label>
                      <div className="mt-1">
                        {isEditingAddress ? (
                          <input
                            type="text"
                            name="country"
                            value={addressData.country}
                            onChange={handleAddressChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        ) : (
                          <div className="py-2">{user.address?.country || '-'}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Form Actions */}
                  {isEditingAddress && (
                    <div className="flex justify-end mt-6 space-x-3">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-800 hover:bg-teal-900"
                      >
                        Save Address
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;